/**
 * Media asset upload to Supabase Storage (Phase 6 slice 2).
 *
 * Uploads a learner-facing media file (image/video/audio) to a public Storage
 * bucket and returns its public URL, which is then stored in a MediaBlock's
 * `url`. Public bucket rationale: media is content meant to be displayed in the
 * editor preview, the runtime (a different origin), and eventually an LMS
 * iframe — a permanent, stable URL works everywhere, whereas signed URLs expire
 * and would silently break a published scenario. Write access is still
 * owner-scoped: files land under `{userId}/...`, and the bucket's INSERT policy
 * restricts writes to the signed-in user's own folder. (See the bucket-setup
 * SQL in the slice-2 notes / README.)
 *
 * This module assumes `supabaseConfigured` is true — callers only reach it in
 * cloud mode (the file picker is hidden in local mode). In local mode authors
 * paste a URL by hand, exactly as before.
 */
import { newId, type MediaKind } from '@branchworks/shared';
import { getSupabaseClient } from './supabaseClient';

/** The Storage bucket name. Must match the bucket created in the dashboard. */
export const MEDIA_BUCKET = 'scenario-media';

/** Max upload size (bytes). Keeps us comfortably under free-tier limits and
 *  rejects accidental huge files early. Tune as plans change. */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024; // 50 MB

/** Accept attribute for the file picker, by media kind. */
export function acceptForKind(kind: MediaKind): string {
  return `${kind}/*`;
}

/** Pull a lowercased file extension (no dot) from a filename, or '' if none. */
function extOf(filename: string): string {
  const i = filename.lastIndexOf('.');
  if (i === -1 || i === filename.length - 1) return '';
  return filename.slice(i + 1).toLowerCase().replace(/[^a-z0-9]/g, '');
}

export interface UploadResult {
  /** Public URL to store in the MediaBlock. */
  url: string;
  /** The storage key (path within the bucket), kept for potential later cleanup. */
  path: string;
}

/**
 * Upload a media file and return its public URL.
 *
 * Path layout: `{userId}/{scenarioId}/{mediaId}.{ext}`
 *   - owner-scoped top folder → the INSERT storage policy can gate on it
 *   - scenario folder → groups a scenario's assets for later bulk cleanup
 *   - mediaId basename → stable, collision-free, ties the file to its block
 *
 * Throws on: not signed in, file too large, or a Storage error.
 */
export async function uploadMediaAsset(
  file: File,
  scenarioId: string,
  mediaId: string,
): Promise<UploadResult> {
  if (file.size > MAX_UPLOAD_BYTES) {
    const mb = Math.round(MAX_UPLOAD_BYTES / (1024 * 1024));
    throw new Error(`File is too large (max ${mb} MB).`);
  }

  const client = getSupabaseClient();

  // Owner id scopes the path so the bucket's INSERT policy can enforce it.
  const {
    data: { user },
    error: userErr,
  } = await client.auth.getUser();
  if (userErr) throw userErr;
  if (!user) throw new Error('Not signed in — cannot upload media.');

  const ext = extOf(file.name);
  // mediaId is already unique per block; suffix a short token so re-uploading
  // to the same block doesn't collide with a cached old object.
  const base = `${mediaId}-${newId('a')}`;
  const path = `${user.id}/${scenarioId}/${ext ? `${base}.${ext}` : base}`;

  const { error: uploadErr } = await client.storage
    .from(MEDIA_BUCKET)
    .upload(path, file, {
      // Each block gets a fresh unique path, so no overwrite needed.
      upsert: false,
      contentType: file.type || undefined,
      cacheControl: '3600',
    });
  if (uploadErr) throw uploadErr;

  const { data } = client.storage.from(MEDIA_BUCKET).getPublicUrl(path);
  return { url: data.publicUrl, path };
}
